import { UploadOutlined } from "@ant-design/icons";
import * as Sentry from "@sentry/browser";
import { Button, Checkbox, Collapse, Divider, message, Spin } from "antd";
import { CheckboxChangeEvent } from "antd/lib/checkbox";
import { CheckboxValueType } from "antd/lib/checkbox/Group";
import { SizeType } from "antd/lib/config-provider/SizeContext";
import { AxiosResponse } from "axios";
import React from "react";
import { ExportedData } from "../helpers/shared-interfaces";
import {
  formatSnakeCase,
  generateDisplayErrorMessage,
  request,
  responseHandler,
} from "../helpers/utilities";
import ModalCreator from "./ModalCreator";

export const EXPORT_MODEL_DATA = `
query ExportModelData($ids:[ID!]!, $model: String!, $exportableFields: JSON){
  exportCsv(ids: $ids, model: $model, exportableFields: $exportableFields) {
    url
    errors
  }
}
`;

export const GENERATE_EXPORT_MODEL_DATA = `
mutation GenerateExportData( $model: String!, $exportableFields: JSON, $ids:[ID!], ){
  generateExportData(model: $model, exportableFields: $exportableFields, ids: $ids) {
    exportData{
      status
      id
      url
    }
    errors {
      property
      description
    }
  }
}
`;

const QUERYABLE_FIELDS_FOR_EXPORT = `
query ExportFields($model: String!) {
  exportFields(model: $model)
}
`;

interface Props {
  model: string;
  dataArray?: ModelData[];
  size?: string;
  customIds?: React.Key[];
  buttonText?: string;
  handleExport?: (selectedFields: Array<QueryableFieldsType>) => void;
  customQueryableFields?: Array<QueryableFieldsType>;
  disabled?: boolean;
  icon?: React.ReactNode;
}

interface ModelData {
  id: string;
}

type QueryableFieldsType = {
  [key: string]: Array<string>;
};

interface State {
  isLoading: boolean;
  queryableFields: Array<QueryableFieldsType>;
  showExportFieldsModal: boolean;
  selectedExportFields: Array<QueryableFieldsType>;
  checkAll: boolean;
  indeterminate: boolean;
}

const { Panel } = Collapse;

export const FieldsToExportUI = (props: {
  exportFields: Array<QueryableFieldsType>;
  selectedFields: Array<QueryableFieldsType>;
  onChange: (values: CheckboxValueType[], checkboxParent: string) => void;
  checkAll: boolean;
  indeterminate: boolean;
  onCheckAllChange: (e: CheckboxChangeEvent) => void;
}): JSX.Element => {
  return (
    <>
      <Divider />
      <Collapse>
        {props.exportFields.map((fieldParent, index) => {
          return (
            <Panel
              header={formatSnakeCase(Object.keys(fieldParent)[0])}
              key={index}
            >
              <Checkbox.Group
                name={`checkbox_${index}`}
                key={index}
                className="custom-checkbox"
                defaultValue={
                  props.selectedFields?.[index]?.[Object.keys(fieldParent)?.[0]]
                }
                onChange={(value) =>
                  props.onChange(value, Object.keys(fieldParent)[0])
                }
                options={fieldParent[Object.keys(fieldParent)[0]].map(
                  (field) => {
                    return { label: formatSnakeCase(field), value: field };
                  },
                )}
              ></Checkbox.Group>
            </Panel>
          );
        })}
      </Collapse>
    </>
  );
};

export default class ExportData extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      isLoading: false,
      queryableFields: [],
      showExportFieldsModal: false,
      selectedExportFields: [],
      checkAll: false,
      indeterminate: true,
    };
  }

  downloadData = async (): Promise<void> => {
    const { dataArray, model } = this.props;
    const { selectedExportFields } = this.state;
    interface Response {
      data: {
        exportCsv: {
          url: string;
        };
      };
    }
    let ids: string[] = [];

    if (this.props.customIds) {
      ids = this.props.customIds as string[];
    } else if (dataArray) {
      for (const data of dataArray) {
        ids.push(data.id);
      }
    }

    this.setState({ isLoading: true });

    try {
      const response: AxiosResponse<Response> = await request({
        query: EXPORT_MODEL_DATA,
        variables: {
          ids,
          model: model,
          exportableFields: JSON.stringify(selectedExportFields),
        },
      });

      const { err, handled } = responseHandler<Response>(
        response as unknown as AxiosResponse,
        "exportCsv",
      );
      this.setState({ isLoading: false, showExportFieldsModal: false });

      if (handled) {
        message.error("An Error Occurred. Your request couldn't be processed.");
        return;
      } else if (err) {
        message.error(`An Error Occurred. - ${generateDisplayErrorMessage(err)}`);
      } else {
        window.open(response.data.data.exportCsv.url);
        message.success("Export Data downloaded sucessfully");
        this.setState({
          selectedExportFields: [],
        });
      }
    } catch (error) {
      message.error(`Failed to Export Data - ${generateDisplayErrorMessage(error)}`);
    }
  };

  exportData = async (): Promise<void> => {
    const { dataArray, model } = this.props;
    const { selectedExportFields } = this.state;
    interface Response {
      exportData: ExportedData;
    }
    let ids: string[] = [];

    if (this.props.customIds) {
      ids = this.props.customIds as string[];
    } else if (dataArray) {
      for (const data of dataArray) {
        ids.push(data.id);
      }
    }

    this.setState({ isLoading: true });
    let variables: any = {
      model: model,
      exportableFields: JSON.stringify(selectedExportFields),
    };

    if (ids.length > 0) {
      variables["ids"] = ids;
    }

    try {
      const response: AxiosResponse<Response> = await request({
        query: GENERATE_EXPORT_MODEL_DATA,
        variables,
      });

      const { err, mutation } = responseHandler<Response>(
        response,
        "generateExportData",
      );

      this.setState({ isLoading: false, showExportFieldsModal: false });

      if (err) {
        message.error(generateDisplayErrorMessage(err));
      }

      if (mutation) {
        message.success("Export Data process started sucessfully");
        this.setState({
          selectedExportFields: [],
        });
      }
    } catch (error) {
      this.setState({ isLoading: false, showExportFieldsModal: false });
    }
  };

  onExport = (): void => {
    const { handleExport } = this.props;
    if (handleExport) {
      handleExport(this.state.selectedExportFields);
      this.toggleExportFieldsModal();
    } else {
      this.exportData();
    }
  };

  toggleExportFieldsModal = (): void => {
    const prevState = this.state.showExportFieldsModal;
    this.setState({
      showExportFieldsModal: !prevState,
      checkAll: false,
      indeterminate: true,
      selectedExportFields: [],
    });
  };

  handleOnCheckAll = (e: CheckboxChangeEvent) => {
    this.setState(
      {
        selectedExportFields: e.target.checked
          ? this.state.queryableFields
          : [],
        indeterminate: false,
        checkAll: e.target.checked,
      },
      this.saveExportableFields,
    );
  };

  checkBoxChange = (
    checkedValues: CheckboxValueType[],
    checkBoxParent: string,
  ): void => {
    const { selectedExportFields } = this.state;
    let newSelectedExportFields = selectedExportFields;

    const selectedFieldObject = { [checkBoxParent]: checkedValues as string[] };

    const index = selectedExportFields.findIndex((obj) =>
      obj.hasOwnProperty(checkBoxParent),
    );

    if (index !== -1) {
      newSelectedExportFields[index][checkBoxParent] = [
        ...newSelectedExportFields[index][checkBoxParent],
        ...(checkedValues as string[]),
      ];
    } else {
      newSelectedExportFields.push(selectedFieldObject);
    }

    this.setState(
      {
        indeterminate:
          !!this.countSelectedCheckboxes(newSelectedExportFields) &&
          this.countSelectedCheckboxes(newSelectedExportFields) <
          this.state.queryableFields.length,
        selectedExportFields: newSelectedExportFields,
        checkAll:
          this.countSelectedCheckboxes(newSelectedExportFields) ===
          this.countSelectedCheckboxes(this.state.queryableFields),
      },
      this.saveExportableFields,
    );
  };

  countSelectedCheckboxes = (fields: Array<QueryableFieldsType>) => {
    let count = 0;
    fields.forEach((item) => {
      count = count += item[Object.keys(item)[0]].length;
    });

    return count;
  };

  getQueryableFields = async (): Promise<void> => {
    const { model, customQueryableFields } = this.props;

    if (customQueryableFields) {
      this.setState(
        {
          queryableFields: customQueryableFields,
          isLoading: false,
          showExportFieldsModal: true,
        },
        () => {
          this.loadSavedExportableFields();
        },
      );
      return;
    }

    this.setState({ isLoading: true });
    try {
      const response: AxiosResponse<any> = await request({
        query: QUERYABLE_FIELDS_FOR_EXPORT,
        variables: {
          model: model,
        },
      });

      const parsedExportFields = JSON.parse(
        response.data.data.exportFields,
      ) as Array<QueryableFieldsType>;

      this.setState(
        {
          queryableFields: parsedExportFields,
          isLoading: false,
          showExportFieldsModal: true,
        },
        () => {
          this.loadSavedExportableFields();
        },
      );
    } catch (error) {
      this.setState({ isLoading: false });
      message.error(`Failed to fetch queryable fields - ${generateDisplayErrorMessage(error)}`);
      Sentry.captureException(error);
    }
  };

  saveExportableFields = () => {
    localStorage.setItem(
      `${this.props.model}_export`,
      JSON.stringify(this.state.selectedExportFields),
    );
  };

  loadSavedExportableFields = () => {
    const savedFields = localStorage.getItem(`${this.props.model}_export`);
    if (savedFields) {
      this.setState({
        selectedExportFields: JSON.parse(savedFields) as QueryableFieldsType[],
      });
    }
  };

  render(): JSX.Element {
    return (
      <>
        <Spin spinning={this.state.isLoading}>
          <Button
            className="btn-secondary"
            size={this.props.size ? (this.props.size as SizeType) : "middle"}
            onClick={this.getQueryableFields}
            icon={this.props.icon || <UploadOutlined />}
            disabled={this.props.disabled}
          >
            {this.props.buttonText || "Export Data"}
          </Button>
          {this.state.showExportFieldsModal &&
            ModalCreator({
              title: "Select Fields To Export",
              okType: "primary",
              okText: "Export Data",
              onOkay: this.onExport,
              showModal: this.state.showExportFieldsModal,
              toggleModal: this.toggleExportFieldsModal,
              showFooter: true,
              isLoading: this.state.isLoading,
              okayButtonProps: {
                disabled: this.state.selectedExportFields.length <= 0,
              },
              inscription: FieldsToExportUI({
                exportFields: this.state.queryableFields,
                onChange: this.checkBoxChange,
                onCheckAllChange: this.handleOnCheckAll,
                checkAll: this.state.checkAll,
                indeterminate: this.state.indeterminate,
                selectedFields: this.state.selectedExportFields,
              }),
            })}
        </Spin>
      </>
    );
  }
}
